import { LocationPickerLocation, LocationPickerPayload } from "@swivl-mono/types";
import { ButtonBack, ButtonNext, CarouselProvider, Dot, Slider } from "pure-react-carousel";
import "pure-react-carousel/dist/react-carousel.es.css";
import React from "react";

import { Dots } from "../../../../components/Dots/Dots";
import { MessageHandlerInterface } from "../../../../messages/MessageHandler";
import { ListItemData } from "../../ListItem";
import { HeaderWithContext } from "./Components/HeaderWithContext";
import { LocationCard, SelectedLocationCell } from "./Components/LocationCells";
import "./LocationPicker.scss";

interface Props {
  item: ListItemData;
  messageHandler: MessageHandlerInterface;
  width: number;
  isActive: boolean;
}

export interface State {
  shouldFocusOnControl?: string;
}

export const LocationPicker: React.FC<Props> = (props) => {
  const { item, messageHandler, width, isActive } = props;
  const [shouldFocusOnControl, setShouldFocusOnControl] = React.useState<string>();

  const createDot = (index) => {
    return (
      <Dot slide={index} key={"dot" + index}>
        <svg height="10" width="10">
          <circle className="swivl-fillPrimary" cx="5" cy="5" r="4" />{" "}
        </svg>
      </Dot>
    );
  };

  const arrow = (isRight: boolean) => {
    return (
      <svg width="24px" height="24px" viewBox="0 0 24 24" version="1.1" xmlns="http://www.w3.org/2000/svg">
        <g stroke="none" transform={isRight ? "scale(-1,1) translate(-24,0)" : ""} strokeWidth="1" fill="none" fillRule="evenodd">
          <g transform="translate(-10.000000, -467.000000)">
            <g transform="translate(10.000000, 467.000000)">
              <circle className="swivl-fillPrimary" cx="12" cy="12" r="12"></circle>
              <polyline
                stroke="#FFFFFF"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                transform="translate(14.000000, 12.000000) rotate(-315.000000) translate(-14.000000, -12.000000) "
                points="10 8 10 16 18 16"
              ></polyline>
            </g>
          </g>
        </g>
      </svg>
    );
  };

  if (item.message.actionTaken) {
    const data = item.message.actionTaken?.data as LocationPickerLocation;
    return <SelectedLocationCell location={data} />;
  }
  if (!isActive) {
    return null;
  }

  const payload: any = item.message.payload as LocationPickerPayload;

  let slides: any = [];
  let dots: any = [];
  let slideGroup: any = [];
  for (let i = 0; i < payload.locations.length; i++) {
    const location = payload.locations[i];
    slideGroup.push(location);
    if (i === payload.locations.length - 1) {
      if (!payload.canChangeZip || slideGroup.length < 3) {
        dots.push(createDot(slides.length));
        slides.push(
          <LocationCard
            key={payload.currentZip + payload.searchRadiusInMeters + "_" + slides.length + "a"}
            locations={[...slideGroup]}
            index={slides.length}
            showSearch={payload.canChangeZip}
            currentZip={payload.currentZip}
            searchRadiusInMeters={payload.searchRadiusInMeters}
            distanceUnit={payload.distanceUnit}
            messageHandler={messageHandler}
            item={item}
            setShouldFocusOnControl={setShouldFocusOnControl}
            shouldFocusOnControl={shouldFocusOnControl}
          />
        );
      } else {
        dots.push(createDot(slides.length));
        slides.push(
          <LocationCard
            key={payload.currentZip + payload.searchRadiusInMeters + "_" + slides.length + "b"}
            locations={[...slideGroup]}
            index={slides.length}
            currentZip={payload.currentZip}
            searchRadiusInMeters={payload.searchRadiusInMeters}
            distanceUnit={payload.distanceUnit}
            messageHandler={messageHandler}
            item={item}
            setShouldFocusOnControl={setShouldFocusOnControl}
            shouldFocusOnControl={shouldFocusOnControl}
          />
        );
        dots.push(createDot(slides.length));
        slides.push(
          <LocationCard
            key={payload.currentZip + payload.searchRadiusInMeters + "_" + slides.length + "c"}
            locations={[]}
            index={slides.length}
            showSearch={true}
            currentZip={payload.currentZip}
            searchRadiusInMeters={payload.searchRadiusInMeters}
            distanceUnit={payload.distanceUnit}
            messageHandler={messageHandler}
            item={item}
            setShouldFocusOnControl={setShouldFocusOnControl}
            shouldFocusOnControl={shouldFocusOnControl}
          />
        );
      }
    } else if (slideGroup.length === 3) {
      dots.push(createDot(slides.length));
      slides.push(
        <LocationCard
          key={payload.currentZip + payload.searchRadiusInMeters + "_" + slides.length + "d"}
          locations={[...slideGroup]}
          index={slides.length}
          currentZip={payload.currentZip}
          searchRadiusInMeters={payload.searchRadiusInMeters}
          distanceUnit={payload.distanceUnit}
          messageHandler={messageHandler}
          item={item}
          setShouldFocusOnControl={setShouldFocusOnControl}
          shouldFocusOnControl={shouldFocusOnControl}
        />
      );
      slideGroup = [];
    }
  }

  if (slides.length === 0) {
    slides.push(
      <LocationCard
        key="noresults"
        isNoResults={true}
        locations={[]}
        index={0}
        showSearch={true}
        currentZip={payload.currentZip}
        searchRadiusInMeters={payload.searchRadiusInMeters}
        distanceUnit={payload.distanceUnit}
        messageHandler={messageHandler}
        item={item}
        setShouldFocusOnControl={setShouldFocusOnControl}
        shouldFocusOnControl={shouldFocusOnControl}
      />
    );
  }

  let className = "scc-LocationPicker";
  let naturalSlideHeight = 225;
  if (slides.length === 1 && slideGroup.length <= 1) {
    className += " scc-shortPicker";
    naturalSlideHeight = 150;
  }

  const header = payload.currentZip ? (
    <HeaderWithContext
      canChangeZip={payload.canChangeZip}
      searchRadiusInMeters={payload.searchRadiusInMeters}
      currentZip={payload.currentZip}
      locations={payload.locations}
      setShouldFocusOnControl={setShouldFocusOnControl}
      distanceUnit={payload.distanceUnit}
    />
  ) : null;

  return (
    <div className={className}>
      <CarouselProvider
        naturalSlideWidth={props.width}
        naturalSlideHeight={naturalSlideHeight}
        totalSlides={slides.length}
        disableKeyboard={true}
        dragEnabled={false}
        className="scc-carouselProvider"
      >
        {header}
        <Slider className="scc-slider">{slides}</Slider>
        <div className="scc-controlsRow">
          <ButtonBack className="scc-nextAndBackButton">{arrow(false)}</ButtonBack>
          <div className="dotGroup">
            <Dots />
          </div>
          <ButtonNext className="scc-nextAndBackButton">{arrow(true)}</ButtonNext>
        </div>
      </CarouselProvider>
    </div>
  );
};
